"use client";

import JSZip from "jszip";
import Image from "next/image";
import { useState } from "react";
import { Download, Loader2, Package } from "lucide-react";

interface Asset {
    name: string;
    path: string;
    format: string;
    resolution: string;
    size: string;
}

const assets: Asset[] = [
    // Logo (Dark)
    { name: "Logo", path: "/press/Logo.svg", format: "SVG", resolution: "Vector", size: "2.6KB" },
    { name: "Logo", path: "/press/Logo.png", format: "PNG", resolution: "1x", size: "85KB" },
    { name: "Logo", path: "/press/Logo@2x.png", format: "PNG", resolution: "2x", size: "211KB" },
    { name: "Logo", path: "/press/Logo@3x.png", format: "PNG", resolution: "3x", size: "351KB" },
    { name: "Logo", path: "/press/Logo.jpg", format: "JPG", resolution: "1x", size: "822KB" },
    { name: "Logo", path: "/press/Logo@2x.jpg", format: "JPG", resolution: "2x", size: "1.1MB" },
    { name: "Logo", path: "/press/Logo@3x.jpg", format: "JPG", resolution: "3x", size: "1.4MB" },

    // Logo (Light)
    { name: "Logo (Light)", path: "/press/Logo-Light.svg", format: "SVG", resolution: "Vector", size: "4.7KB" },
    { name: "Logo (Light)", path: "/press/Logo-Light.png", format: "PNG", resolution: "1x", size: "75KB" },
    { name: "Logo (Light)", path: "/press/Logo-Light@2x.png", format: "PNG", resolution: "2x", size: "190KB" },
    { name: "Logo (Light)", path: "/press/Logo-Light@3x.png", format: "PNG", resolution: "3x", size: "320KB" },

    // Wortmarke (Dark)
    { name: "Wortmarke", path: "/press/Wortmarke.svg", format: "SVG", resolution: "Vector", size: "2.5KB" },
    { name: "Wortmarke", path: "/press/Wortmarke.png", format: "PNG", resolution: "1x", size: "37KB" },
    { name: "Wortmarke", path: "/press/Wortmarke@2x.png", format: "PNG", resolution: "2x", size: "82KB" },
    { name: "Wortmarke", path: "/press/Wortmarke@3x.png", format: "PNG", resolution: "3x", size: "135KB" },
    { name: "Wortmarke", path: "/press/Wortmarke.jpg", format: "JPG", resolution: "1x", size: "717KB" },
    { name: "Wortmarke", path: "/press/Wortmarke@2x.jpg", format: "JPG", resolution: "2x", size: "811KB" },
    { name: "Wortmarke", path: "/press/Wortmarke@3x.jpg", format: "JPG", resolution: "3x", size: "918KB" },

    // Wortmarke (Light)
    {
        name: "Wortmarke (Light)",
        path: "/press/Wortmarke-Light.svg",
        format: "SVG",
        resolution: "Vector",
        size: "1.7KB",
    },
    { name: "Wortmarke (Light)", path: "/press/Wortmarke-Light.png", format: "PNG", resolution: "1x", size: "32KB" },
    { name: "Wortmarke (Light)", path: "/press/Wortmarke-Light@2x.png", format: "PNG", resolution: "2x", size: "72KB" },
    {
        name: "Wortmarke (Light)",
        path: "/press/Wortmarke-Light@3x.png",
        format: "PNG",
        resolution: "3x",
        size: "120KB",
    },
];

export default function PressPage() {
    const [imageError, setImageError] = useState<Record<string, boolean>>({});
    const [downloading, setDownloading] = useState<Record<string, boolean>>({});
    const [downloadingAll, setDownloadingAll] = useState(false);

    const handleDownload = (asset: Asset) => {
        const link = document.createElement("a");
        link.href = asset.path;
        link.download = asset.path.split("/").pop() || "";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleDownloadAll = async (name: string, groupAssets: Asset[]) => {
        setDownloading((prev) => ({ ...prev, [name]: true }));
        const zip = new JSZip();

        try {
            const fetchPromises = groupAssets.map(async (asset) => {
                const response = await fetch(asset.path);
                const blob = await response.blob();
                const filename = asset.path.split("/").pop() || "";
                zip.file(filename, blob);
            });

            await Promise.all(fetchPromises);

            const content = await zip.generateAsync({ type: "blob" });
            const link = document.createElement("a");
            link.href = URL.createObjectURL(content);
            link.download = `${name.toLowerCase().replace(/\s+/g, "-")}-assets.zip`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);
        } catch (error) {
            console.error("Error downloading assets:", error);
        } finally {
            setDownloading((prev) => ({ ...prev, [name]: false }));
        }
    };

    const handleDownloadAllAssets = async () => {
        setDownloadingAll(true);
        const zip = new JSZip();

        try {
            const fetchPromises = assets.map(async (asset) => {
                const response = await fetch(asset.path);
                const blob = await response.blob();
                const filename = asset.path.split("/").pop() || "";
                zip.file(filename, blob);
            });

            await Promise.all(fetchPromises);

            const content = await zip.generateAsync({ type: "blob" });
            const link = document.createElement("a");
            link.href = URL.createObjectURL(content);
            link.download = "arkadia-all-assets.zip";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);
        } catch (error) {
            console.error("Error downloading all assets:", error);
        } finally {
            setDownloadingAll(false);
        }
    };

    const groupedAssets = assets.reduce(
        (acc, asset) => {
            const key = asset.name;
            if (!acc[key]) {
                acc[key] = [];
            }
            acc[key].push(asset);
            return acc;
        },
        {} as Record<string, Asset[]>,
    );

    return (
        <div className="w-full">
            <div className="border-b border-(--border) py-12 md:py-16 px-6">
                <div className="max-w-3xl mx-auto text-center">
                    <p className="text-xs font-medium tracking-[0.2em] uppercase text-(--muted-fg) mb-3">
                        Brand
                    </p>
                    <h1 className="font-(family-name:--font-instrument-serif) text-3xl md:text-4xl text-foreground mb-4">
                        Press Assets
                    </h1>
                    <p className="text-(--muted-fg) leading-relaxed mb-8">
                        Download our official logo and wordmark assets in various formats and resolutions.
                    </p>
                    <button
                        type="button"
                        onClick={handleDownloadAllAssets}
                        disabled={downloadingAll}
                        className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-lg text-white bg-(--accent-teal) hover:bg-(--accent-teal)/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                    >
                        {downloadingAll ? (
                            <>
                                <Loader2 size={16} className="animate-spin" />
                                Downloading All Assets...
                            </>
                        ) : (
                            <>
                                <Package size={16} />
                                Download All Assets
                            </>
                        )}
                    </button>
                </div>
            </div>

            <div className="max-w-4xl mx-auto px-6 py-10 md:py-14 space-y-10">
                {Object.entries(groupedAssets).map(([name, groupAssets]) => (
                    <div key={name}>
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-lg font-semibold text-foreground">{name}</h2>
                            <button
                                type="button"
                                onClick={() => handleDownloadAll(name, groupAssets)}
                                disabled={downloading[name]}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-(--border) text-(--muted-fg) hover:text-foreground hover:border-(--foreground)/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                            >
                                {downloading[name] ? (
                                    <>
                                        <Loader2 size={12} className="animate-spin" />
                                        Downloading...
                                    </>
                                ) : (
                                    <>
                                        <Download size={12} />
                                        Download All
                                    </>
                                )}
                            </button>
                        </div>
                        <div className="bg-white rounded-xl border border-(--border) divide-y divide-(--border) overflow-hidden">
                            {groupAssets.map((asset) => (
                                <div
                                    key={asset.path}
                                    className="flex items-center justify-between p-4 hover:bg-(--muted)/50 transition-colors"
                                >
                                    <div className="flex items-center gap-4">
                                        <div
                                            className={`relative w-14 h-14 rounded-lg flex items-center justify-center ${
                                                asset.name.includes("Light")
                                                    ? "bg-foreground"
                                                    : "bg-(--muted)"
                                            }`}
                                        >
                                            {!imageError[asset.path] ? (
                                                <Image
                                                    src={asset.path}
                                                    alt={`${asset.name} ${asset.format} ${asset.resolution}`}
                                                    width={40}
                                                    height={40}
                                                    className="object-contain"
                                                    onError={() =>
                                                        setImageError((prev) => ({
                                                            ...prev,
                                                            [asset.path]: true,
                                                        }))
                                                    }
                                                    priority={asset.format === "SVG"}
                                                />
                                            ) : (
                                                <span className="text-(--muted-fg) text-[10px]">
                                                    No preview
                                                </span>
                                            )}
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-foreground">
                                                {asset.format}
                                            </p>
                                            <p className="text-xs text-(--muted-fg)">
                                                {asset.resolution} · {asset.size}
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => handleDownload(asset)}
                                        className="text-(--accent-teal) hover:text-(--accent-teal)/70 text-sm font-medium transition-colors cursor-pointer p-2 rounded-md hover:bg-(--accent-teal-light)"
                                    >
                                        Download
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
